import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import EmployeesTable from "../components/EmployeesTable";
import PageSectionCard from "../components/PageSectionCard";
import Pagination from "../components/Pagination";
import { useAppSession } from "../context/AppSessionContext";
import { fetchAllClientCompanies } from "../lib/clientCompanies";
import { readErrorMessage } from "../lib/http";
import { ClientCompany, Workstation } from "../types/clientCompanies";
import {
  Employee,
  EmployeeLocationType,
  employeeLocationTypeOptions,
  formatEmployeeLocationType,
} from "../types/employees";
import { PaginatedResponse } from "../types/pagination";

const defaultPageSize = 20;
const pageSizeOptions = [10, 20, 50];

function EmployeesListPage() {
  const { t } = useTranslation();
  const { authorizedFetch, canMaintainEmployees } = useAppSession();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<ClientCompany[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [isLoadingWorkstations, setIsLoadingWorkstations] = useState(false);
  const [workstationsError, setWorkstationsError] = useState<string | null>(
    null,
  );

  const [search, setSearch] = useState("");
  const [clientCompanyId, setClientCompanyId] = useState("");
  const [workstationId, setWorkstationId] = useState("");
  const [locationType, setLocationType] = useState<EmployeeLocationType | "">(
    "",
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadEmployees = useCallback(async () => {
    setIsLoadingEmployees(true);
    setEmployeesError(null);

    const query = new URLSearchParams({
      includeInactive: "true",
      page: String(page),
      limit: String(limit),
    });

    const trimmedSearch = search.trim();
    if (trimmedSearch.length > 0) {
      query.set("search", trimmedSearch);
    }

    if (clientCompanyId) {
      query.set("clientCompanyId", clientCompanyId);
    }

    if (workstationId) {
      query.set("workstationId", workstationId);
    }

    if (locationType) {
      query.set("locationType", locationType);
    }

    try {
      const response = await authorizedFetch(`/employees?${query.toString()}`);

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load employees (${response.status}).`,
          ),
        );
      }

      const payload = (await response.json()) as PaginatedResponse<Employee>;
      const responseTotalPages = payload.totalPages ?? 0;

      if (responseTotalPages > 0 && page > responseTotalPages) {
        setPage(responseTotalPages);
        return;
      }

      setEmployees(payload.data ?? []);
      setTotal(payload.total ?? 0);
      setTotalPages(responseTotalPages);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load employees.";
      setEmployeesError(message);
      setEmployees([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [
    authorizedFetch,
    page,
    limit,
    search,
    clientCompanyId,
    workstationId,
    locationType,
  ]);

  const loadCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    setCompaniesError(null);

    try {
      setCompanies(await fetchAllClientCompanies(authorizedFetch));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load client companies.";
      setCompaniesError(message);
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (!clientCompanyId) {
      setWorkstations([]);
      setWorkstationsError(null);
      return;
    }

    const loadWorkstations = async () => {
      setIsLoadingWorkstations(true);
      setWorkstationsError(null);

      try {
        const response = await authorizedFetch(
          `/client-companies/${clientCompanyId}/workstations`,
        );

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              `Failed to load workstations (${response.status}).`,
            ),
          );
        }

        setWorkstations((await response.json()) as Workstation[]);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load workstations.";
        setWorkstationsError(message);
        setWorkstations([]);
      } finally {
        setIsLoadingWorkstations(false);
      }
    };

    void loadWorkstations();
  }, [authorizedFetch, clientCompanyId]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const onCompanyChange = (value: string) => {
    setClientCompanyId(value);
    setWorkstationId("");
    setPage(1);
  };

  const onWorkstationChange = (value: string) => {
    setWorkstationId(value);
    setPage(1);
  };

  const onLocationTypeChange = (value: EmployeeLocationType | "") => {
    setLocationType(value);
    setPage(1);
  };

  const onLimitChange = (value: string) => {
    const nextLimit = Number.parseInt(value, 10);
    setLimit(Number.isNaN(nextLimit) ? defaultPageSize : nextLimit);
    setPage(1);
  };

  const companySelectId = "employees-company-filter";
  const workstationSelectId = "employees-workstation-filter";
  const locationTypeSelectId = "employees-location-filter";
  const pageSizeSelectId = "employees-page-size";

  const showEmptyState =
    !isLoadingEmployees && !employeesError && employees.length === 0;

  const selectedCompanyLabel = useMemo(() => {
    const companyName =
      companies.find((company) => company.id === clientCompanyId)?.name ??
      "all companies";
    const workstationName = workstations.find(
      (workstation) => workstation.id === workstationId,
    )?.name;

    return workstationName
      ? `${companyName} / ${workstationName}`
      : companyName;
  }, [clientCompanyId, companies, workstationId, workstations]);

  return (
    <PageSectionCard
      titleId="employees-list-title"
      eyebrow="Employees"
      title="Employees"
      lead="Create and maintain employee records across client companies."
      actions={
        <div className="headerActions">
          {canMaintainEmployees ? (
            <Link className="primaryButton" to="/employees/new">
              Create
            </Link>
          ) : (
            <span className="primaryButton disabledAction">Create</span>
          )}
        </div>
      }
    >
      <div className="listControls" role="group" aria-label="Employee filters">
        <label className="field">
          <span className="fieldLabel">Search</span>
          <input
            className="inputControl"
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Name or national ID"
          />
        </label>

        <label className="field" htmlFor={companySelectId}>
          <span className="fieldLabel">Client Company</span>
          <select
            className="inputControl"
            id={companySelectId}
            value={clientCompanyId}
            onChange={(event) => onCompanyChange(event.target.value)}
            disabled={isLoadingCompanies}
          >
            <option value="">All companies</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor={workstationSelectId}>
          <span className="fieldLabel">Workstation</span>
          <select
            className="inputControl"
            id={workstationSelectId}
            value={workstationId}
            onChange={(event) => onWorkstationChange(event.target.value)}
            disabled={!clientCompanyId || isLoadingWorkstations}
          >
            <option value="">
              {clientCompanyId ? "All workstations" : "Select a company first"}
            </option>
            {workstations.map((workstation) => (
              <option key={workstation.id} value={workstation.id}>
                {workstation.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor={locationTypeSelectId}>
          <span className="fieldLabel">{t("Location")}</span>
          <select
            className="inputControl"
            id={locationTypeSelectId}
            value={locationType}
            onChange={(event) =>
              onLocationTypeChange(
                event.target.value as EmployeeLocationType | "",
              )
            }
          >
            <option value="">All locations</option>
            {employeeLocationTypeOptions.map((option) => (
              <option key={option} value={option}>
                {formatEmployeeLocationType(option)}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor={pageSizeSelectId}>
          <span className="fieldLabel">Page size</span>
          <select
            className="inputControl"
            id={pageSizeSelectId}
            value={String(limit)}
            onChange={(event) => onLimitChange(event.target.value)}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoadingEmployees ? <p>Loading employees...</p> : null}

      {employeesError ? (
        <p className="errorMessage" role="alert">
          {employeesError}
        </p>
      ) : null}

      {companiesError ? (
        <p className="errorMessage" role="alert">
          Unable to load client-company filter options: {companiesError}
        </p>
      ) : null}

      {workstationsError ? (
        <p className="errorMessage" role="alert">
          Unable to load workstation filter options: {workstationsError}
        </p>
      ) : null}

      {showEmptyState ? (
        <div className="emptyState">
          <p>
            No employees found for {selectedCompanyLabel}
            {search.trim() ? ` matching "${search.trim()}".` : "."}
          </p>
          {canMaintainEmployees ? (
            <p className="hintText">
              Use the Create button above to add an employee.
            </p>
          ) : (
            <p className="hintText">
              Ask a secretary/manager/admin to create employees.
            </p>
          )}
        </div>
      ) : null}

      {!isLoadingEmployees && !employeesError && employees.length > 0 ? (
        <EmployeesTable
          employees={employees}
          canMaintainEmployees={canMaintainEmployees}
        />
      ) : null}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={isLoadingEmployees}
        summary={`${total} employee${total === 1 ? "" : "s"}`}
      />
    </PageSectionCard>
  );
}

export default EmployeesListPage;
